<template>
    <div class="total-overview">
        <div class="total-overview__container total-overview__desktop-content" v-if="!isMobile">
            <div class="total-overview__cart-items">
                <CartItems :is-desktop="false" />

                <hr class="total-overview__divider" />
            </div>

            <div class="total-overview-dropdown__item-description" v-if="firstItemDescription">
                <CartItemDescription 
                    :item-description="firstItemDescription"
                />
                <hr class="total-overview__divider" />
            </div>

            <div class="total-overview__shipping-info" v-if="shippingMethod && showShippingMethod">
                <strong class="total-overview__title" v-text="$ml.get('delivery')" tabindex="0" />
                <ShippingMethodOverview :minimal="false" />
                <hr class="total-overview__divider" />
            </div>

            <div class="total-overview__payment-info" v-if="showPaymentInfo">
                <strong class="total-overview__title" v-text="$ml.get('payment')" tabindex="0" />
                <CreditCardOverview />
                <BoletoOverview />
                <PixOverview />
                <PixOpenFinanceOverview />
                <QrCodePayOverview />
                <ClickToPayOverview />
                <GooglePayOverview />
                <ApplePayOverview />

                <hr class="total-overview__divider" />
            </div>

            <div class="total-overview__payment-date" v-if="showPaymentDate">
                <strong class="total-overview__title" v-text="$ml.get('paymentDate')" tabindex="0" />
                <PaymentDateOverview />
                <hr class="total-overview__divider" />
            </div>
        </div>

        <div ref="totalHeader" class="total-overview__container">
            <span class="total-overview__total-text" v-text="$ml.get('totalAmount')" tabindex="0" />

            <button @click="shouldShowContent = !shouldShowContent" class="total-overview__toggle-button" :aria-label="isMobile ? $ml.get('ariaShowOrderSummaryContent') : ''" :tabindex="isMobile ? '0' : '-1'">
                <span class="total-overview__total-price" :tabindex="isMobile ? '-1' : '0'">
                    R$ {{ formattedPrice }}
                </span>

                <img :class="{ active: shouldShowContent }" src="@/assets/img/icons/arrow-down-blue.svg" alt="" tabindex="-1" />
            </button>
        </div>

        <transition name="fade" v-if="isMobile">
            <div :style="{ top: headerHeight }" v-if="shouldShowContent" class="total-overview-dropdown">
                <div @click.stop v-if="shouldShowContent" class="content total-overview-dropdown__container">
                    <div class="total-overview-dropdown__merchant-info" v-if="showMerchantName">
                        <hr class="total-overview__divider" />

                        <span class="total-overview-dropdown__merchant-name" tabindex="0">{{ merchantName }}</span>

                        <hr class="total-overview__divider" />
                    </div>


                    <hr class="total-overview__divider" v-else />

                    <div class="total-overview-dropdown__cart-items">
                        <CartItems />

                        <hr class="total-overview__divider" />
                    </div>

                    <div class="total-overview-dropdown__item-description" v-if="firstItemDescription">
                        <CartItemDescription
                            :is-short-description="true"
                            :item-description="firstItemDescription"
                        />
                        <hr class="total-overview__divider" />
                    </div>

                    <!-- Section: Entrega -->
                    <div v-if="shippingMethod && showShippingMethod" class="total-overview-dropdown__shipping-info">
                        <strong class="total-overview__title" v-text="$ml.get('delivery')" tabindex="0" />
                        <ShippingMethodOverview :minimal="false" />
                        <hr class="total-overview__divider" />
                    </div>

                    <!-- Section: Pagamento -->
                    <div v-if="showPaymentInfo" class="total-overview-dropdown__payment-info">
                        <strong class="total-overview__title" v-text="$ml.get('payment')" tabindex="0" />
                        <CreditCardOverview />
                        <BoletoOverview />
                        <PixOverview />
                        <PixOpenFinanceOverview />
                        <QrCodePayOverview />
                        <ClickToPayOverview />
                        <GooglePayOverview />
                        <ApplePayOverview />
                        <hr class="total-overview__divider" />
                    </div>

                    <div class="total-overview__payment-date" v-if="showPaymentDate">
                        <strong class="total-overview__title" v-text="$ml.get('paymentDate')" tabindex="0" />
                        <PaymentDateOverview />
                        <hr class="total-overview__divider" />
                    </div>
                </div>

                <div
                    @click="shouldShowContent = false"
                    :style="{ top: headerHeight }"
                    class="overlay total-overview-dropdown__overlay"
                />
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CreditCardOverview from '@/components/OrderOverview/components/CreditCardOverview';
import BoletoOverview from '@/components/OrderOverview/components/BoletoOverview';
import PixOverview from '@/components/OrderOverview/components/PixOverview';
import PixOpenFinanceOverview from '@/components/OrderOverview/components/PixOpenFinanceOverview';
import ShippingMethodOverview from '@/components/OrderOverview/components/ShippingMethodOverview';
import QrCodePayOverview from '@/components/OrderOverview/components/QrCodePayOverview';
import ClickToPayOverview from '@/components/OrderOverview/components/ClickToPayOverview';
import GooglePayOverview from '@/components/OrderOverview/components/GooglePayOverview';
import ApplePayOverview from '@/components/OrderOverview/components/ApplePayOverview';
import CartItems from '@/components/CartItems';
import CartItemDescription from '@/components/CartItemDescription';
import PaymentDateOverview from '@/components/OrderOverview/components/PaymentDateOverview';

export default {
    data() {
        return {
            shouldShowContent: false,
            firstItemDescription: '',
        };
    },

    props: {
        isMobile: {
            type: Boolean,
            default: false
        },
        showMerchantName: {
            type: Boolean,
            default: true
        }
    },

    created() {
        this.firstItemDescription = this.getFirstItemDescription;
    },

    computed: {
        ...mapGetters({
            formattedPrice: 'payment/getFormattedPrice',
            selectedPaymentMethod: 'payment/getSelectedPaymentMethod',
            shippingMethod: 'shippingType/getMethod',
            showShippingMethod: 'shippingType/getShowShippingMethod',
            installments: 'payment/getInstallmentNumberOfPayments',
        }),

        ...mapState({
            merchantName: state => state.preorder.MerchantName,
            items: state => state.cart.Items,
        }),

        headerHeight() {
            let element = this.$refs.totalHeader;

            return element.clientHeight + element.offsetTop - 1 + 'px';
        },

        getFirstItemDescription() {
            const { Description } = this.items.find((item) => item.Description) || {};
            
            return Description;
        },

        showPaymentInfo() {
          if(this.$store.state.payment.PaymentType === 100) {
              return this.selectedPaymentMethod && !!this.$store.state.clickToPayPayment.checkoutResponse.cardArtUri;
          }

          if(this.$store.state.payment.PaymentType === 101) {
              return this.selectedPaymentMethod && !!this.$store.state.googlePayPayment.selectedCard.description;
          }
          
          if(this.$store.state.payment.PaymentType === 102) {
              return !!this.installments;
          }

          return this.selectedPaymentMethod;
        },

        showPaymentDate() {
            if(this.$store.state.payment.PaymentType === 9) {
                return this.selectedPaymentMethod && this.$store.state.pixOpenFinancePayment.paymentDate;
            }

            return false;
        }
      },

    components: {
        CreditCardOverview,
        ShippingMethodOverview,
        BoletoOverview,
        PixOverview,
        QrCodePayOverview,
        ClickToPayOverview,
        GooglePayOverview,
        ApplePayOverview,
        CartItems,
        CartItemDescription,
        PixOpenFinanceOverview,
        PaymentDateOverview
    }
};
</script>

<style lang="scss" scoped>
.total-overview {
    background: #EFF1F3;
    /* position: relative; */

    &__container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 0;

        @media screen and (min-width: 1025px) {
            padding: 24px;
        }
    }

    &__desktop-content {
        flex-direction: column;
        align-items: normal;
        padding-bottom: 0;

        &+.total-overview__container {
            padding-top: 8px;
        }
    }

    &__title {
        font-size: 16px;
        font-weight: 600;
        line-height: 16px;
        color: #000;
        margin-bottom: 20px;
        display: block;
    }

    &__total-text {
        font-size: 16px;
        line-height: 16px;
        color: #000;
        font-weight: 700;

        @media screen and (min-width: 1025px) {
            font-size: 20px;
            line-height: 20px;
        }
    }

    &__toggle-button {
        background: none;
        border: none;

        @media screen and (min-width: 1025px) {
            pointer-events: none;
        }

        img {

            @media screen and (min-width: 1025px) {
                display: none;
            }

            &.active {
                transform: rotate(180deg);
            }

        }
    }

    &__total-price {
        font-size: 16px;
        line-height: 16px;
        font-weight: 700;
        color: #0667CD;

        @media screen and (min-width: 1025px) {
            font-size: 20px;
            line-height: 20px;
            color: #000;
        }
    }

    &__divider {
        border-color: #B8C1CC;
        margin: 16px 0;
    }
}

.total-overview-dropdown {
    position: absolute;
    left: 0;
    width: 100%;
    z-index: 10;

    &__container {
        position: relative;
        top: -16px;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        background-color: #EFF1F3;
        padding: 0 16px;
    }

    &__overlay {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        background: RGBA(32, 31, 32, 0.6);
        z-index: 1;
    }

    &__merchant-info {}

    &__merchant-name {
        font-size: 16px;
        font-weight: 700;
        line-height: 16px;
        color: #000;
    }

    &__cart-info {}

    &__shipping-info {}

    &__payment-info {}

}

//Transition component
.fade-enter-active,
.fade-leave-active {
    transition: opacity .3s;
}

.fade-enter,
.fade-leave-to
/* .fade-leave-active em versões anteriores a 2.1.8 */
  {
    opacity: 0;
  }

//Transition component
</style>
